import { gql } from "@apollo/client";
import { useMutation, useQuery } from "@apollo/client/react";
import { useEffect, useState } from "react";
import { IoCodeSlashOutline, IoCreateOutline } from "react-icons/io5";

import ReadCategory from "./ReadCategory.graphql";
import UpsertCategory from "./UpsertCategory.graphql";
import useNotifications, { notifyMutationErrors } from "../../../../../components/Notifications";
import * as UI from "../../../../../modules/ui-kit";
import CodeEditor from "../../../components/CodeEditor";
import { StyledEditorLayout } from "../../../components/StyledEditorLayout";
import { sameTranslations } from "../../../modules/compare";
import { useUnsavedChangesEditor } from "../../../modules/unsaved-changes";
import categoryDefinition, { withoutTypenames } from "../categoryDefinition";

export default function CategoryEditor(props) {
    const { guard, id } = props;

    const notifications = useNotifications();

    const [mode, setMode] = useState<"form" | "json">("form");

    const readCategory = useQuery(
        gql(ReadCategory),
        {
            variables: {
                id,
            },
        },
    );

    const [upsertCategory] = useMutation(
        gql(UpsertCategory),
        {
            awaitRefetchQueries: true,
            refetchQueries: [
                gql(ReadCategory),
                "ListCategories",
            ],
            variables: {
                id,
            },
        },
    );

    // `categorySource`, not `category`: the latter is the English-filtered copy
    // the title and side list render, and editing that would drop every other
    // language on save.
    const storedSource = readCategory.data?.categorySource ?? {};
    const storedName = storedSource.name ?? [];
    const storedIcon = storedSource.icon ?? "";

    const [name, setName] = useState(undefined);
    const [icon, setIcon] = useState(undefined);
    const [jsonText, setJsonText] = useState("");
    const [jsonError, setJsonError] = useState(undefined);
    const [jsonParsed, setJsonParsed] = useState(undefined);
    const [saving, setSaving] = useState(false);



    const isDataLoaded = Boolean(readCategory.data?.categorySource && readCategory.data?.additionalCategoryData);

    const storedDefinitionText = isDataLoaded
        ? JSON.stringify(categoryDefinition(readCategory.data, {}), null, 2)
        : "";

    // Keyed on the serialised document, not on `readCategory.data`: three
    // components on this screen share ReadCategory and two of them refetch it,
    // and every refetch hands back a new object even when nothing changed —
    // which used to wipe whatever was typed in the JSON editor.
    useEffect(
        () => {
            setName(undefined);
            setIcon(undefined);
            setJsonText(storedDefinitionText);
            setJsonError(undefined);
            setJsonParsed(undefined);
        },
        [id, storedDefinitionText],
    );

    const currentName = name ?? storedName;
    const currentIcon = icon ?? storedIcon;

    const dirty = mode === "form"
        ? (!sameTranslations(currentName, storedName) || currentIcon !== storedIcon)
        : (jsonParsed !== undefined && isDataLoaded && JSON.stringify(jsonParsed, null, 2) !== storedDefinitionText);

    function onJsonChange(value: string | undefined) {
        const text = value ?? "";
        setJsonText(text);
        try {
            const parsed = JSON.parse(text);
            setJsonParsed(parsed);
            setJsonError(undefined);
        } catch (e) {
            setJsonError((e as Error).message);
        }
    }

    function switchMode(targetMode: "form" | "json") {
        setJsonError(undefined);
        if (targetMode === "json" && isDataLoaded) {
            const def = categoryDefinition(readCategory.data, {
                icon: currentIcon || undefined,
                name: currentName,
            });
            setJsonText(JSON.stringify(def, null, 2));
        }
        setMode(targetMode);
    }

    async function save() {
        // The Save button for this screen lives in CategoryDetail and can't see
        // this editor's JSON state, so it stays enabled — say why nothing
        // happened instead of failing silently.
        if (mode === "json" && jsonError) {
            notifications.error(`Invalid JSON: ${ jsonError }`);
            return false;
        }

        setSaving(true);

        try {
            const definitionPayload = mode === "json" && jsonParsed
                ? withoutTypenames(jsonParsed)
                : categoryDefinition(
                    readCategory.data,
                    {
                        icon: currentIcon || undefined,
                        name: currentName.map(
                            (translation) => (
                                {
                                    language: translation.language,
                                    text: translation.text,
                                }
                            )
                        ),
                    },
                );

            const result = await upsertCategory(
                {
                    variables: {
                        definition: definitionPayload,
                    },
                }
            );

            if (result.data?.upsertCategory?.errors?.length) {
                notifyMutationErrors(notifications, result.data.upsertCategory.errors);
                return false;
            }

            setName(undefined);
            setIcon(undefined);
            setJsonParsed(undefined);

            notifications.success("Saved.");

            return true;
        } finally {
            setSaving(false);
        }
    }

    function onReset() {
        setName(undefined);
        setIcon(undefined);
        setJsonText(storedDefinitionText);
        setJsonError(undefined);
        setJsonParsed(undefined);
    }

    // Reset/Save for this screen live in CategoryDetail, below all three
    // sections, because Save commits every registered editor — not just this
    // form. Registering `onReset` is what lets that button reach in here.
    useUnsavedChangesEditor(guard, save, dirty, onReset);

    if (readCategory.loading) return "loading";
    if (readCategory.error) return "error";

    // Enter in a text field still submits; route it through the guard so it
    // behaves like the page-level Save button.
    function onSubmit(event) {
        event.preventDefault();
        if (guard) void guard.saveRef.current?.();
        else void save();
    }


    return (
        <StyledEditorLayout>
            <div className="mode-tabs">
                <button
                    className={ mode === "form" ? "active" : "" }
                    onClick={ () => switchMode("form") }
                    type="button"
                >
                    <IoCreateOutline /> Form View
                </button>
                <button
                    className={ mode === "json" ? "active" : "" }
                    onClick={ () => switchMode("json") }
                    type="button"
                >
                    <IoCodeSlashOutline /> JSON Source
                </button>
            </div>

            <UI.Form
                className="editor-body"
                onReset={ onReset }
                onSubmit={ onSubmit }
            >
                { mode === "form" ? (
                    <>
                        <UI.Field>
                            <UI.FieldLabel>name</UI.FieldLabel>
                            <UI.FieldDescription>It is sufficient to fill in just one (arbitrary) language when the name is the same in all languages</UI.FieldDescription>
                            <UI.TranslatedInput
                                disabled={ saving }
                                label="name"
                                onChange={ setName }
                                value={ currentName }
                            />
                        </UI.Field>
                        <UI.Field>
                            <UI.FieldLabel>icon</UI.FieldLabel>
                            <UI.FieldDescription>SVG shown next to the category in the app menu. Only main (first level) categories use an icon — subcategories leave this empty.</UI.FieldDescription>
                            <UI.TextInput
                                disabled={ saving }
                                onChange={ setIcon }
                                placeholder="https://..."
                                value={ currentIcon }
                            />
                            { currentIcon && <img alt="" className="preview" src={ currentIcon } /> }
                        </UI.Field>
                    </>
                ) : (
                    <UI.Field className="json-field">
                        <UI.FieldLabel>Raw Category Definition</UI.FieldLabel>
                        <UI.FieldDescription>Edit raw category JSON definition (name, icon, public, bimClasses, parentCategory)</UI.FieldDescription>
                        <div className="json-editor-wrapper">
                            <CodeEditor
                                key={ `${ id }-${ mode }-${ isDataLoaded }` }
                                onChange={ onJsonChange }
                                value={ jsonText }
                            />
                        </div>
                        { jsonError && <span className="errors">Invalid JSON: { jsonError }</span> }
                    </UI.Field>
                ) }
            </UI.Form>
        </StyledEditorLayout>
    );
}
